import { v4 as uuidv4 } from 'uuid';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isEmpty } from 'radash';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getDeletesForSegmentPure } from '@goldcast/player-config/src/utils/timeUtils';
import { COMPOSITION_DIMENSIONS, VIDEO_QUALITY } from '@goldcast/player-config/src/constants/Remotion.constants';
import { FitType, LayoutType } from '@goldcast/player-config/src/types/asset-types';
import { Scene, Scenes } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Scene';
import { getVideosAndTimeRanges } from '@goldcast/player-config/src/utils/videoRangeUtils';
import { Sequence } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Sequence';
import { CompositeSegment } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import { getSegmentsTrack } from '@goldcast/player-config/src/utils/playerConfigGetters';
import {
  getAllCompositeSegments,
  getEndForSegment,
  getStartForSegment,
  isClipInOriginalLanguage,
  isSplitsInTimelineEnabledPure
} from '@goldcast/player-config/src/utils/clipUtils';
import { useMenuVisibility, MENU_BREAKPOINTS_WITH_DRAG } from '../../hooks/useMenuVisibility';
import TimelineScenes from '../TimelineScenes/TimelineScenes';
import Deletes from '../Deletes';
import TotalCompositeDuration from '../TotalCompositeDuration';
import TimelineSpeakers from '../TimelineSpeakers';
import TimelineSelect from '../TimelineSelect';
import TrimHandles from './TrimHandles';
import TimelineEffectsLane from '../../TimelineEffects/TimelineEffectsLane';
import { TIMELINE_COMPONENT_CLASS_IDENTIFIER } from '../../constants';
import { useRemoveVideoSegment } from '../../hooks/useRemoveVideoSegment';
import { getVisibleTrimHandle } from './utils';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { classnames } from '@/libs/utils';
import { isFullVideoFaceRecEnabled, isFullVideoFaceRecForPreRecordedEnabled } from '@/stores/clip';
import { useUploadEditorReadinessContext } from '@/context/UploadEditorReadinessContext/UploadEditorReadinessContext';
import { useContentMetadataStore } from '@/stores/contentMetaDataStore/contentMetaDataStore';
import Icon from '@/components/atoms/Icon';

function TimelineCompositeSegment({
  setCompositeSegmentDragStart,
  setCompositeSegmentDragEnd,
  compositeSegmentDragStart,
  compositeSegmentDragEnd,
  newTimelineContainerRef,
  speakerTimelineContainerRef,
  setCompositeSegmentDragId,
  compositeSegment,
  compositeTimelineRef,
  compositeSegmentDragId
}: {
  setCompositeSegmentDragStart: (value: number) => void;
  setCompositeSegmentDragEnd: (value: number) => void;
  compositeSegmentDragStart: number;
  compositeSegmentDragEnd: number;
  newTimelineContainerRef: React.RefObject<HTMLDivElement>;
  speakerTimelineContainerRef: React.RefObject<HTMLDivElement>;
  setCompositeSegmentDragId: (value: string | null) => void;
  compositeSegment: CompositeSegment;
  compositeTimelineRef: React.RefObject<HTMLDivElement>;
  compositeSegmentDragId: string | null;
}) {
  const [showTrimHandlesOnHover, setShowTrimHandlesOnHover] = useState<'start' | 'end' | null>(null);
  const { clipData, clipId, fps } = useClipsContext();
  const { screenshareTerminal } = useUploadEditorReadinessContext();
  const speakerTracksHidden = !screenshareTerminal;
  const isTrimmingSomeOtherSegment = useMemo(
    () => compositeSegmentDragId && compositeSegmentDragId !== compositeSegment.id,
    [compositeSegmentDragId, compositeSegment.id]
  );
  const isTrimmingCurrentSegment = useMemo(
    () => compositeSegmentDragId === compositeSegment.id,
    [compositeSegmentDragId, compositeSegment.id]
  );

  const removeVideoSegment = useRemoveVideoSegment(clipId, compositeSegment.id);

  const playerConfig = clipData.asset_metadata.player_config;
  const totalSegmentCount = useMemo(
    () => (playerConfig && getSegmentsTrack(playerConfig).segments.length) || 0,
    [playerConfig]
  );
  const currentSegmentIndex = useMemo(
    () =>
      playerConfig && getSegmentsTrack(playerConfig).segments.findIndex(segment => segment.id === compositeSegment.id),
    [playerConfig, compositeSegment]
  );
  const isFirstSegment = useMemo(() => currentSegmentIndex === 0, [currentSegmentIndex]);
  const isLastSegment = useMemo(
    () => currentSegmentIndex === totalSegmentCount - 1,
    [currentSegmentIndex, totalSegmentCount]
  );

  const isTranslated = useMemo(() => {
    return !isClipInOriginalLanguage(clipData);
  }, [clipData]);

  const compositeSegmentRef = useRef<HTMLDivElement | null>(null);
  const [showDragHandle, setShowDragHandle] = useState(false);
  const menuVisibility = useMenuVisibility(compositeSegmentRef, MENU_BREAKPOINTS_WITH_DRAG);

  const showTime = menuVisibility === 'all';
  const showTrash = !isTranslated && (menuVisibility === 'all' || menuVisibility === 'no-time');
  const showDrag =
    !isTranslated && (menuVisibility === 'all' || menuVisibility === 'no-time' || menuVisibility === 'drag-only');

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: compositeSegment.id,
    disabled: false
  });

  const style = {
    transform: CSS.Transform.toString(transform ? { ...transform, scaleX: 1, scaleY: 1, y: 0 } : null),
    transition
  };

  const isSplitsEnabled = useMemo(() => {
    return isSplitsInTimelineEnabledPure({
      clip: clipData,
      isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
    });
  }, [clipData]);

  const isMenuVisible = useMemo(() => {
    if (menuVisibility === 'none') {
      return false;
    }
    if (menuVisibility === 'all') {
      return true;
    }
    if (isTranslated && !showTrash && !showDrag) {
      return false;
    }
    return true;
  }, [menuVisibility, isTranslated, showTrash, showDrag]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!compositeSegmentRef.current) {
        return;
      }

      const rect = compositeSegmentRef.current.getBoundingClientRect();
      const mouseX = e.clientX;
      const segmentLeft = rect.left;
      const segmentRight = rect.right;
      const hoverThreshold = 20; // pixels from edge to trigger hover
      const trimBarsWidth = 18;

      // Check if mouse is near the left or right edge
      const isNearLeftEdge =
        mouseX >= segmentLeft - (showTrimHandlesOnHover ? trimBarsWidth : 0) && mouseX <= segmentLeft + hoverThreshold;
      const isNearRightEdge =
        mouseX <= segmentRight + (showTrimHandlesOnHover ? trimBarsWidth : 0) &&
        mouseX >= segmentRight - hoverThreshold;

      setShowTrimHandlesOnHover(
        getVisibleTrimHandle(isNearLeftEdge, isNearRightEdge, mouseX, segmentLeft, segmentRight)
      );
    };

    const handleMouseLeave = () => {
      setShowTrimHandlesOnHover(null);
    };

    const element = compositeSegmentRef.current;
    if (element) {
      element.addEventListener('mousemove', handleMouseMove);
      element.addEventListener('mouseleave', handleMouseLeave);
    }

    return () => {
      if (element) {
        element.removeEventListener('mousemove', handleMouseMove);
        element.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, [showTrimHandlesOnHover]);

  const timelineSelectionEnabled = !isSplitsEnabled;
  // Calculate timeline width and duration
  const timelineDuration =
    getEndForSegment({
      clip: clipData,
      segmentId: compositeSegment.id,
      isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
    }) -
    getStartForSegment({
      clip: clipData,
      segmentId: compositeSegment.id,
      isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
    });

  const effects = useMemo(() => compositeSegment?.effects || [], [compositeSegment?.effects]);
  // TODO: @AshwinBhatkal - to be removed upon implementing clip creation on BE - 91dag8e1yg8a678e29
  const scenes: Scenes = useMemo(() => {
    if (!compositeSegment) {
      return [];
    }

    const scenes = compositeSegment.scenes;

    if (scenes.length === 0) {
      const all_video_ids = getVideosAndTimeRanges({
        clip: clipData,
        isFullFaceRecEnabled: isFullVideoFaceRecEnabled(),
        isFullFaceRecForPreRecordedEnabled: isFullVideoFaceRecForPreRecordedEnabled(),
        contentMetadataStoreInstance: useContentMetadataStore
      }).map(video => video.videoId);
      const defaultScene: Scene = {
        id: uuidv4(),
        name: 'Scene 1',
        fromFrame: compositeSegment.currentBounds.fromFrame,
        toFrame: compositeSegment.currentBounds.toFrame,
        details: {
          layout: clipData.asset_metadata.layout as LayoutType,
          fitMode: clipData.asset_metadata.fit as FitType,
          visible_video_ids: all_video_ids,
          all_video_ids: all_video_ids
        }
      };

      return [defaultScene];
    }

    return scenes;
    // Other dependencies not require - if we set scene once, we don't need the other dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compositeSegment?.scenes]);

  const compositeSegmentDeleteBounds: Sequence[] = useMemo(() => {
    const segmentDeletes = getDeletesForSegmentPure({
      clip: clipData,
      segmentId: compositeSegment.id,
      isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
    });
    if (segmentDeletes && !isEmpty(segmentDeletes)) {
      return Object.values(segmentDeletes)
        .map(d => ({
          fromFrame: d.bounds[0] * fps,
          toFrame: d.bounds[1] * fps
        }))
        .sort((a, b) => a.fromFrame - b.fromFrame)
        .filter(d => d.fromFrame !== d.toFrame);
    }

    return [];
  }, [clipData, compositeSegment.id, fps]);

  const { compositionHeight, compositionWidth } = useMemo(() => {
    const videoSize = clipData.asset_metadata.size;

    const videoQuality = VIDEO_QUALITY.FULL_HD;
    const compositionHeight = COMPOSITION_DIMENSIONS[videoSize][videoQuality].HEIGHT;
    const compositionWidth = COMPOSITION_DIMENSIONS[videoSize][videoQuality].WIDTH;

    return {
      compositionHeight,
      compositionWidth
    };
  }, [clipData.asset_metadata.size]);

  const recordingHasEffects = useMemo(() => {
    return getAllCompositeSegments(clipData).some(segment => segment.effects?.length && segment.effects?.length > 0);
  }, [clipData]);

  const setNodeRefForCompositeSegment = useCallback(
    (node: HTMLDivElement | null) => {
      setNodeRef(node);
      compositeSegmentRef.current = node;
    },
    [setNodeRef]
  );
  const canDeleteCompositeSegment = useMemo(() => {
    return getAllCompositeSegments(clipData).length > 1 && isSplitsEnabled;
  }, [clipData, isSplitsEnabled]);

  // Used to force composite time and hover effect when cursor is outside of the segment during drag
  const forceShowHoverItems =
    !isTrimmingSomeOtherSegment && (showDragHandle || showTrimHandlesOnHover || isTrimmingCurrentSegment);

  return (
    <div
      ref={setNodeRefForCompositeSegment}
      style={{
        width: `calc(${
          compositeSegment
            ? (compositeSegment.currentBounds.toFrame / fps +
                compositeSegmentDragEnd -
                (compositeSegment.currentBounds.fromFrame / fps + compositeSegmentDragStart)) *
              fps
            : 0
        }px * var(--timeline-zoom))`,
        paddingLeft: isFirstSegment ? '0px' : '1.5px',
        paddingRight: isLastSegment ? '0px' : '1.5px',
        ...style
      }}
      className={classnames('group/composite-segment relative', isDragging ? 'z-50 opacity-50' : '')}
      data-testid="timeline-composite-segment"
      onMouseDown={e => {
        if (isDragging) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      onMouseEnter={() => setShowDragHandle(true)}
      onMouseLeave={() => setShowDragHandle(false)}
    >
      <div className="relative size-full">
        <div
          className={classnames(
            'absolute inset-0 rounded-[7px] border-2 border-slate-200',
            forceShowHoverItems ? 'border-slate-950' : ''
          )}
        ></div>
        <div className="relative flex flex-col gap-1 overflow-hidden py-[4px]" ref={compositeTimelineRef}>
          {effects?.length > 0 ? (
            <TimelineEffectsLane
              effects={effects}
              duration={timelineDuration}
              newTimelineContainerRef={newTimelineContainerRef}
              speakerTimelineContainerRef={speakerTimelineContainerRef}
              startOffsetSeconds={getStartForSegment({
                clip: clipData,
                segmentId: compositeSegment.id,
                isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
              })}
            />
          ) : recordingHasEffects ? (
            <div className="flex w-full flex-col overflow-hidden" style={{ pointerEvents: 'auto' }}>
              <div className="relative flex h-[32px] min-h-6 w-full items-center"></div>
            </div>
          ) : null}
          {compositeSegment ? (
            <TimelineScenes
              scenes={scenes}
              compositeSegment={compositeSegment}
              compositeSegmentDragStart={compositeSegmentDragStart}
              compositeSegmentDragEnd={compositeSegmentDragEnd}
              videoSize={clipData.asset_metadata.size}
              showBorder={!clipData.asset_metadata.hide_borders}
              playerConfig={clipData.asset_metadata.player_config}
              compositeSegmentDeleteBounds={compositeSegmentDeleteBounds}
              newTimelineContainerRef={newTimelineContainerRef}
              speakerTimelineContainerRef={speakerTimelineContainerRef}
              compositionHeight={compositionHeight}
              compositionWidth={compositionWidth}
            />
          ) : (
            <div className="mx-1 h-[30px] rounded-[3px] bg-slate-200"></div>
          )}
          {compositeSegment && !speakerTracksHidden && (
            <TimelineSpeakers
              compositeSegmentDragStart={compositeSegmentDragStart}
              compositeSegmentDragEnd={compositeSegmentDragEnd}
              compositeSegment={compositeSegment}
            />
          )}
          {speakerTracksHidden && (
            <div className="flex items-center justify-center gap-2 py-3 text-sm text-slate-400">
              <Icon name="IconLoader2" className="size-4 shrink-0 animate-spin" />
              Audio tracks and screenshare will be available after video is analysed.
            </div>
          )}
          <Deletes
            compositeSegment={compositeSegment}
            compositeSegmentDragStart={compositeSegmentDragStart}
            compositeSegmentDragEnd={compositeSegmentDragEnd}
            newTimelineContainerRef={newTimelineContainerRef}
            speakerTimelineContainerRef={speakerTimelineContainerRef}
          />
          {timelineSelectionEnabled && (
            <TimelineSelect
              compositeSegment={compositeSegment}
              compositeSegmentRef={compositeSegmentRef}
              speakerTimelineContainerRef={speakerTimelineContainerRef}
            />
          )}
        </div>
        {forceShowHoverItems && !isDragging && isMenuVisible && (
          <div className={classnames('pointer-events-none sticky bottom-[8px] z-50 flex h-0 items-end justify-end')}>
            <div
              className={classnames(
                'pointer-events-auto mr-[10px] flex w-fit items-center gap-0 rounded-md bg-black px-1.5 py-1 text-white opacity-0 shadow-lg transition-opacity duration-200 group-hover/composite-segment:opacity-100 group-hover:opacity-100',
                TIMELINE_COMPONENT_CLASS_IDENTIFIER.ELEMENT_MENU_CONTAINER
              )}
              style={{
                opacity: forceShowHoverItems ? 1 : undefined
              }}
            >
              {showTime && (
                <div className="mr-1 text-xs text-white">
                  <TotalCompositeDuration compositeSegment={compositeSegment} />
                </div>
              )}
              {isSplitsEnabled && showDrag && (
                <div
                  {...attributes}
                  {...listeners}
                  className="flex cursor-move items-center justify-center rounded p-1.5 text-white transition-all duration-200 hover:bg-white/10 active:cursor-grabbing"
                  data-testid="composite-segment-drag-handle"
                >
                  <Icon name="IconArrowsMove" size={12} className="text-white" />
                </div>
              )}
              {isSplitsEnabled && showTrash && (
                <div
                  onClick={e => {
                    if (canDeleteCompositeSegment) {
                      e.stopPropagation();
                      removeVideoSegment();
                    }
                  }}
                  className={classnames(
                    'group/delete segment-delete-btn flex cursor-pointer items-center justify-center rounded p-1.5 text-white transition-all duration-200',
                    canDeleteCompositeSegment ? '' : 'cursor-not-allowed opacity-50'
                  )}
                  data-testid="composite-segment-delete-button"
                >
                  <Icon
                    name="IconTrash"
                    size={12}
                    className="text-white transition-colors duration-200 group-hover/delete:text-color-highlight-timeline"
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {compositeSegment && !isTranslated && (
        <TrimHandles
          fps={fps}
          clipId={clipId}
          clipData={clipData}
          compositeSegment={compositeSegment}
          showTrimsBars={isTrimmingSomeOtherSegment ? null : showTrimHandlesOnHover}
          speakerTimelineContainerRef={speakerTimelineContainerRef}
          compositeSegmentDragStart={compositeSegmentDragStart}
          compositeSegmentDragEnd={compositeSegmentDragEnd}
          setCompositeSegmentDragStart={setCompositeSegmentDragStart}
          setCompositeSegmentDragEnd={setCompositeSegmentDragEnd}
          setCompositeSegmentDragId={setCompositeSegmentDragId}
        />
      )}
    </div>
  );
}

export default memo(TimelineCompositeSegment);
